// S-3 — HER CIRCLE, IN MY ROOM. TWO COUNTS, AND THE LIST BEHIND EACH OF THEM.
//
// **THE BODY IS HOOKLESS ON PURPOSE, WHICH IS R64's RULING ONE ROOM OVER.** The decision this
// surface actually makes is a FOUR-WAY one — still loading / could not be read / genuinely nobody /
// here they are — and inside a stateful component that fourth-of-four is reachable only by a live
// browser. `CircleList` takes props and returns a tree, so `scripts/s3-circle.mjs` CALLS it once per
// state and the state nobody thinks about is the one a gate can drive.
//
// **THE ACT LIVES ON THE ROW AND THE TWO ROWS ARE NOT THE SAME ACT.** From FOLLOWING she unfollows,
// which is ITEM 7's own route and is never blocked. From FOLLOWERS she REMOVES, which destroys a row
// belonging to somebody else and is silent by her order. They read differently on purpose: an
// unfollow is a thing she does to her own list, a removal is a thing she does to somebody.
//
// **LR17-2 — AND THE FOLLOWERS ROW NOW CARRIES THE SHARED FOLLOW CONTROL BESIDE THE REMOVE.** Her
// order: *"every row in the My Room FOLLOWERS list carries the shared follow-state button (gold
// Follow / green Followed), so she follows back WITHOUT NEEDING THEM TO POST."* That last clause is
// the load-bearing one and it is already satisfied by construction here: this list is read straight
// off `lr_follows` and never through `resolveMemberRef`, which requires a member to have POSTED
// because it exists to stop the @picker becoming a directory — so a follower who has never written a
// word in the room is on this list, and the tag on her row posts her opaque `ref` to ITEM 7's own
// route.
//
// **THAT LAST CLAUSE USED TO END "which does not consult that resolver either", AND IT WAS FALSE.**
// LR17-3's walk found it: `handleFollow`'s FOLLOW arm resolved through `resolveMemberRef` too, so
// the follow-back this clause is about was REFUSED for exactly the follower it names — the one who
// has never posted — silently, wearing the room's one unreachable sentence. The route asks
// `resolveMemberId` now (*is this a member*), and the directory question stays on the doors it
// belongs to. A comment claiming a law is held is a claim; this one was a false receipt for two
// rounds (R125/R141's class, in prose).
//
// **IT IS `FollowTag` ITSELF, NOT A SECOND CONTROL WEARING ITS COLOURS.** A follow button written
// here would be a second answer to what a follow looks like, what a tap means, and what happens to
// the eleven other copies of that person on the screen (R28/R29) — and the broadcast is the half a
// copy always loses: `FollowTag` listens on `FOLLOW_EVENT`, so a follow made in the room moves this
// row and a follow made HERE moves the room.
// **LR17-3 — AND THE SAME BLOCK NOW FINDS PEOPLE SHE IS NOT ALREADY CONNECTED TO.** Her spec:
// house-styled, an `@tag` or a plain name with or without the `@`, room names AND first names, rows
// carrying the follow button — **finding and following only, no doors to anyone's room or chart.**
// The row is `CircleRow` rather than a second row written beside it, so the avatar, the crown, the
// ellipsis law and the tag's placement outside it are one answer for all three lists (R28/R29); what
// differs is the ACT, and it is a REQUIRED prop rather than an optional one, so the type refuses a
// call site that forgets to decide (14-B6a's ruling — an optional capability prop is R27's own live
// defect). A search row's act is `null`, in as many words: this surface does nothing TO anybody.
import { useCallback, useEffect, useRef, useState } from "react";
import { Search, UserMinus, UserX, X } from "lucide-react";
import { Avatar, OwnerCrown, FollowTag, FOLLOW_EVENT, LivingRoomMe, type Identity } from "./LivingRoomShared";
import { ModalBackdrop } from "./ModalBackdrop";
import {
  memberSearchTerm, memberSearchState, memberSearchEmpty, memberSearchCapNote,
  MEMBER_SEARCH_PLACEHOLDER, MEMBER_SEARCH_LABEL, MEMBER_SEARCH_LOADING, MEMBER_SEARCH_LIMIT,
} from "../livingRoom.js";
import {
  loadFollowers, loadFollowing, loadCircleCounts, removeFollower, unfollow, searchMembers,
  countLabel, hasMore, CIRCLE_READ_FAILED,
  type CircleCounts, type CirclePerson,
} from "../lib/follow-lists";

export type CircleKind = "followers" | "following";

const TITLES: Record<CircleKind, string> = { followers: "My Followers", following: "Following" };
// The empty states are two different facts about her and are worded as two (R64: the failure state
// is not a missing sentence but a wrong one — telling her nobody follows her over a read that never
// landed is a statement about her rather than about the request).
const EMPTY: Record<CircleKind, string> = {
  followers: "Nobody is following you yet.",
  following: "You are not following anyone yet.",
};
const ACT_LABEL: Record<CircleKind, string> = { followers: "Remove", following: "Unfollow" };

// ---------------------------------------------------------------------------------------------
// THE TWO COUNTS. Hookless, so a gate CALLS them — including with counts that have not arrived,
// which is the state that draws a dash rather than a zero.
// ---------------------------------------------------------------------------------------------
export function FollowCounts({
  counts, onOpen,
}: { counts: CircleCounts | null; onOpen: (kind: CircleKind) => void }) {
  return (
    <div className="circle-counts">
      {(["followers", "following"] as CircleKind[]).map((kind) => (
        <button
          key={kind}
          type="button"
          className="circle-count"
          onClick={() => onOpen(kind)}
          aria-label={`${TITLES[kind]}, ${countLabel(counts?.[kind])}`}
        >
          <strong className="circle-count__n">{countLabel(counts?.[kind])}</strong>
          <span className="circle-count__label">{TITLES[kind]}</span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// LR17-3 — **ONE ROW, THREE LISTS.** The avatar, the `@name`, the crown, the ellipsis and the tag's
// placement OUTSIDE the ellipsised name are one answer wherever a person is drawn in this block. The
// act is a REQUIRED prop and `null` is a real value for it: the search results do nothing to anybody,
// and a required prop is what makes that a decision the type asks for rather than one a call site can
// forget (R27's live defect was a capability behind an optional prop neither call site passed).
// ---------------------------------------------------------------------------------------------
export function CircleRow({ who, act }: { who: CirclePerson; act: React.ReactNode }) {
  return (
    <li className="circle-row">
      <span className="circle-row__who">
        <Avatar who={who as unknown as Identity} size="md" />
        <span className="circle-row__name">
          @{who.name}
          <OwnerCrown who={who as unknown as Identity} />
        </span>
        {/* LR17-2 — the shared tag, beside the name rather than INSIDE it, and that is a
            measurement rather than a preference: `.circle-row__name` ellipsises a long room
            name (`overflow: hidden`), so a tag nested in it is a control a member with a long
            name can never reach. It draws nothing at all when `me` is unknown or when the row
            is her own, which is ITEM 7's three-state law rather than a condition written
            here. */}
        <FollowTag who={who as unknown as Identity} />
      </span>
      {act}
    </li>
  );
}

// ---------------------------------------------------------------------------------------------
// THE LIST. Four states, and the order they are asked in is the law: the read that did not land
// sits ABOVE the empty one, held by POSITION (R52/R65), because a check that only asks whether a
// sentence exists passes on a screen printing both.
// ---------------------------------------------------------------------------------------------
export function CircleList({
  kind, people, note, loading, total, busyRef, onAct, onMore,
}: {
  kind: CircleKind;
  people: CirclePerson[] | null;
  note: string;
  loading: boolean;
  total: number | null;
  busyRef: string;
  onAct: (person: CirclePerson) => void;
  onMore: () => void;
}) {
  if (loading && people === null) return <p className="circle-note">Loading...</p>;
  if (people === null) return <p className="circle-note">{note || CIRCLE_READ_FAILED}</p>;
  if (!people.length) return <p className="circle-empty">{EMPTY[kind]}</p>;
  return (
    <>
      {note ? <p className="circle-note" role="status">{note}</p> : null}
      <ul className="circle-list">
        {people.map((who) => (
          <CircleRow
            key={who.ref}
            who={who}
            act={(
              <button
                type="button"
                className={kind === "followers" ? "circle-act circle-act--remove" : "circle-act circle-act--unfollow"}
                disabled={busyRef === who.ref}
                onClick={() => onAct(who)}
                aria-label={`${ACT_LABEL[kind]} @${who.name}`}
              >
                {kind === "followers" ? <UserX size={14} /> : <UserMinus size={14} />}
                {busyRef === who.ref ? "..." : ACT_LABEL[kind]}
              </button>
            )}
          />
        ))}
      </ul>
      {hasMore(people.length, total) ? (
        <button type="button" className="circle-more" onClick={onMore} disabled={loading}>
          {loading ? "Loading..." : `Show more (${people.length} of ${total})`}
        </button>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// LR17-3 — **WHAT THE SEARCH FOUND. FIVE STATES, AND IDLE IS THE ONE A LIST DOES NOT HAVE.**
//
// Hookless, so a gate CALLS it once per state (R64's Cauldron ruling, which is why `CircleList` is
// shaped this way too) — and the state nobody thinks about is exactly the one that costs: a box she
// has not typed in is not a search that found nobody, and `memberSearchState` is where that order is
// decided rather than in a ternary a gate cannot reach.
//
// **THE ROWS CARRY NO ACT AT ALL AND THAT IS HER ORDER, HELD AS A PROPERTY RATHER THAN AS RESTRAINT.**
// *Finding and following only, no doors to anyone's room or chart.* The follow is `FollowTag`, which
// `CircleRow` mounts for every list in this block; there is nothing else on the row to press.
// ---------------------------------------------------------------------------------------------
export function MemberResults({
  term, people, loading, note,
}: { term: string; people: CirclePerson[] | null; loading: boolean; note: string }) {
  const state = memberSearchState({ term, people, loading });
  if (state === "idle") return null;
  if (state === "loading") return <p className="circle-note">{MEMBER_SEARCH_LOADING}</p>;
  if (state === "failed") return <p className="circle-note">{note || CIRCLE_READ_FAILED}</p>;
  if (state === "none") return <p className="circle-empty">{memberSearchEmpty(term)}</p>;
  const found = people || [];
  const capped = memberSearchCapNote(found.length);
  return (
    <>
      <ul className="circle-list circle-list--found">
        {found.map((who) => <CircleRow key={who.ref} who={who} act={null} />)}
      </ul>
      {/* R17/R160 — no silent caps. A page of exactly the limit and a page that found exactly the
          limit are the same picture, and the repair (type another letter) is one she can only take
          if she is told there is more behind it. */}
      {capped ? <p className="circle-note" role="status">{capped}</p> : null}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// THE MOUNT. It owns the state and nothing else: every decision above it is a function this file
// exports, and every answer it paints comes from the server (R39).
// ---------------------------------------------------------------------------------------------
export function FollowCircle({ isMember }: { isMember: boolean }) {
  const [counts, setCounts] = useState<CircleCounts | null>(null);
  const [open, setOpen] = useState<CircleKind | "">("");
  const [people, setPeople] = useState<CirclePerson[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyRef, setBusyRef] = useState("");
  // LR17-2 — who is looking, straight from the list's own answer rather than from a second round
  // trip. `""` until a page lands, which is what makes the tag draw NOTHING rather than draw a wrong
  // one while the list is still arriving (R18/R44: refuse, never resolve).
  //
  // **LR17-3 — AND IT IS STILL ONE STATE, FILLED BY WHICHEVER ANSWER ARRIVED FIRST.** The search
  // route sends the same opaque ref for the same viewer, so a second `searchMe` beside this one would
  // be two answer keys for one fact about the caller (R28/R29) — and the provider below wraps the
  // WHOLE block, because ITEM 7's ruling is that the grant is per SCREEN.
  const [me, setMe] = useState("");
  // LR17-3 — the find-someone box.
  const [term, setTerm] = useState("");
  const [found, setFound] = useState<CirclePerson[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchNote, setSearchNote] = useState("");

  const doFetch = useCallback((url: string, init?: unknown) => fetch(url, init as RequestInit), []);

  const refreshCounts = useCallback(async () => {
    const c = await loadCircleCounts(doFetch);
    // R84's lesson on the mail badge: a count is a fact about her, so a read that did not land
    // leaves the last one she was actually told standing rather than replacing it with a zero.
    if (c) setCounts(c);
  }, [doFetch]);

  useEffect(() => { if (isMember) refreshCounts(); }, [isMember, refreshCounts]);

  // ---------------------------------------------------------------------------------------------
  // LR17-3 — **THE SEARCH RUNS AS SHE TYPES, DEBOUNCED, AND THE ANSWER IS DISCARDED IF IT IS STALE.**
  //
  // The debounce is what stops a keystroke being a database read (13-F1's five-connection pool) —
  // R3's library search already works this way. **The `useRef` is the half that has no symptom**: a
  // debounce cannot cancel a request already in flight, so a slow answer for `"ni"` landing after a
  // fast one for `"nia"` repaints the list with rows for a term she has stopped typing — the right
  // people for the wrong question, with nothing red anywhere. A `useState` guard reads the value from
  // the render that bound the closure, which is D-5's own finding one control over; a ref is read at
  // the moment the answer arrives, which is the moment the question is.
  // ---------------------------------------------------------------------------------------------
  const asked = useRef("");
  useEffect(() => {
    const q = memberSearchTerm(term);
    asked.current = q;
    // An empty box is IDLE rather than a search that found nobody, so nothing is asked and nothing
    // is claimed. `null` is what `memberSearchState` reads back as "not loaded".
    if (!q) { setFound(null); setSearching(false); setSearchNote(""); return undefined; }
    setSearching(true);
    const timer = window.setTimeout(async () => {
      const answer = await searchMembers(doFetch, q, MEMBER_SEARCH_LIMIT);
      if (asked.current !== q) return;
      setSearching(false);
      // Kept only when the server really sent one (LR17-2's rule): a search that did not land must
      // not blank the ref the rows already on screen are drawn against, which silently retires every
      // follow control in the block.
      if (answer.me) setMe(answer.me);
      setFound(answer.people);
      setSearchNote(answer.note);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [term, doFetch]);

  const page = useCallback(async (kind: CircleKind, offset: number) => {
    setLoading(true);
    const answer = await (kind === "followers" ? loadFollowers : loadFollowing)(doFetch, 30, offset);
    setLoading(false);
    if (answer.counts) setCounts(answer.counts);
    // Kept only when the server really sent one: a later page that did not land must not blank the
    // ref the rows already on screen are being drawn against, which would silently retire every
    // follow control in the list.
    if (answer.me) setMe(answer.me);
    setTotal(answer.total);
    setNote(answer.note);
    // A page that did not land must not wipe the rows already on screen: the first page is the only
    // one whose failure means there is nothing to show.
    if (answer.people === null) { if (offset === 0) setPeople(null); return; }
    setPeople((prev) => (offset === 0 || prev === null ? answer.people : [...prev, ...(answer.people || [])]));
  }, [doFetch, people]);

  const openList = useCallback((kind: CircleKind) => {
    setOpen(kind); setPeople(null); setTotal(null); setNote("");
    page(kind, 0);
  }, [page]);

  const act = useCallback(async (person: CirclePerson) => {
    if (!open) return;
    setBusyRef(person.ref);
    const answer = open === "followers"
      ? await removeFollower(doFetch, person.ref)
      : await unfollow(doFetch, person.ref);
    setBusyRef("");
    if (answer.counts) setCounts(answer.counts);
    if (!answer.done) { setNote(answer.note); return; }
    setNote("");
    // ITEM 7's window event, so every copy of that person on the screen moves at once. It is
    // dispatched for the UNFOLLOW only: a removal changes THEIR graph, not the tag on hers.
    //
    // **AND IT IS IMPORTED, NEVER TYPED** (R66/R68's shared-literal rule). The first draft of this
    // line hand-typed "living-room:follow-changed"; the declaration says `12h:follow`, so the sync
    // her order asks for would have fired into nothing, silently, with no error state anywhere —
    // R38's trap on an event name.
    if (open === "following") window.dispatchEvent(new CustomEvent(FOLLOW_EVENT));
    setPeople((prev) => (prev || []).filter((p) => p.ref !== person.ref));
    setTotal((n) => (typeof n === "number" ? Math.max(0, n - 1) : n));
    // **THE COUNTS ARE RE-ASKED AFTER EITHER ACT, AND THE WALK IS WHAT FOUND THAT THEY WERE NOT.**
    // The removal's own reply carries them, so only the unfollow needed this — and it needed it
    // because `/api/living-room/follow` is ITEM 7's hot path, tapped by every follow tag in the feed,
    // and hanging two counts off it would put an extra query on the widest surface in the room to
    // serve one panel in My Room. So the cost is paid HERE, once, after an act that landed, rather
    // than everywhere (13-F1's argument about what a per-item read costs against this pool).
    refreshCounts();
  }, [open, doFetch, refreshCounts]);

  if (!isMember) return null;
  return (
    // LR17-2/LR17-3 — **THE GRANT IS PER SCREEN, WHICH IS ITEM 7's OWN RULING AND NOT A DETAIL.**
    // `FollowTag` asks this context who the viewer is; the alternative — a prop on the row — is R27's
    // live defect exactly, a capability that renders only where somebody remembered to pass something.
    // Saying it ONCE here gives every name this block draws the tag: the two lists inside the modal
    // and the search results outside it, from one fact and one provider.
    <LivingRoomMe.Provider value={me || null}>
      <FollowCounts counts={counts} onOpen={openList} />
      {/* LR17-3 — HER FIND-SOMEONE BOX, BESIDE THE TWO COUNTS. A form, so Enter is a submit rather
          than a page reload; the search itself is the debounced effect above, so the button is a
          thumb-sized affordance rather than a second way to ask (R28/R29 — one thing decides when a
          search happens). `type="search"` gives a phone its own clear control for free. */}
      <form className="circle-search" role="search" onSubmit={(e) => e.preventDefault()}>
        <label className="circle-search__label" htmlFor="circle-search-field">{MEMBER_SEARCH_LABEL}</label>
        <div className="circle-search__field">
          <Search size={15} aria-hidden="true" />
          <input
            id="circle-search-field"
            type="search"
            className="circle-search__input"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={MEMBER_SEARCH_PLACEHOLDER}
            autoComplete="off"
          />
        </div>
        <MemberResults term={term} people={found} loading={searching} note={searchNote} />
      </form>
      {open ? (
        <ModalBackdrop label={TITLES[open]} className="circle-modal-backdrop" onClose={() => setOpen("")}>
          <div className="circle-modal">
            <button className="modal-close" type="button" onClick={() => setOpen("")} aria-label="Close">
              <X size={18} />
            </button>
            <h2 className="circle-modal__title">{TITLES[open]}</h2>
            {/* LR17-3 — **THE SECOND PROVIDER THAT USED TO STAND HERE IS GONE, AND THAT IS THE ONE
                THING TO KNOW ABOUT THIS MODAL.** `ModalBackdrop` PORTALS to `<body>`, so the tag
                inside it is a DOM sibling of the block — but React context follows the React tree
                rather than the DOM one, so the provider wrapping this whole component reaches it.
                Two providers for one fact is R28/R29 with the collision invisible: they agree today
                because they read the same state, and the day one of them is fed a second `me` the
                two halves of this block disagree about who is looking. */}
            <CircleList
              kind={open}
              people={people}
              note={note}
              loading={loading}
              total={total}
              busyRef={busyRef}
              onAct={act}
              onMore={() => page(open, (people || []).length)}
            />
          </div>
        </ModalBackdrop>
      ) : null}
    </LivingRoomMe.Provider>
  );
}
